import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Instagram, Facebook, Youtube, Globe } from 'lucide-react';
import { socialMedia } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';

// TikTok Icon Component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Language Selector Component
const LanguageSelector = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language, changeLanguage } = useLanguage();

  const languages = [
    { code: 'it', label: 'Italiano', flag: '🇮🇹', display: 'IT' },
    { code: 'en', label: 'English', flag: '🇬🇧', display: 'EN' }
  ];

  const currentLangDisplay = languages.find(l => l.code === language)?.display || 'IT';

  const handleLanguageChange = (code) => {
    changeLanguage(code);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 text-white hover:text-cyan-400 transition-colors text-sm font-medium"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4" />
        <span>{currentLangDisplay}</span>
      </button>
      
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-black/95 border border-zinc-700 rounded-md overflow-hidden min-w-[140px] shadow-xl z-50">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              data-testid={`lang-${lang.code}`}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center gap-2 hover:bg-zinc-800 transition-colors ${
                language === lang.code ? 'text-cyan-400 bg-zinc-800/50' : 'text-white'
              }`}
            >
              <span>{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: t('home') },
    { path: '/biography', label: t('biography') },
    { path: '/horses', label: t('horses') },
    { path: '/calendar', label: t('calendar') },
    { path: '/news', label: t('news') },
    { path: '/blog', label: t('blog') },
    { path: '/photos', label: t('photos') },
    { path: '/shop', label: t('shop') },
    { path: '/sponsors', label: t('sponsors') }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-black/95 backdrop-blur-md shadow-lg py-3' 
          : 'bg-transparent py-5'
      }`}
    >
      <div className="container mx-auto px-4 md:px-8 lg:px-16">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="transition-transform duration-300 hover:scale-105"
          >
            <img 
              src="/logo-krew.png" 
              alt="KREW Logo" 
              className={`transition-all duration-300 ${isScrolled ? 'h-20' : 'h-32'} w-auto`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`text-sm font-bold tracking-wider uppercase transition-all duration-300 relative group ${
                  isActive(link.path) 
                    ? 'text-pink-500' 
                    : 'text-white hover:text-cyan-400'
                }`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 transition-all duration-300 ${
                    isActive(link.path) 
                      ? 'w-full bg-pink-500' 
                      : 'w-0 group-hover:w-full bg-cyan-400'
                  }`}
                />
              </Link>
            ))}
            <a
              href={socialMedia.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-instagram"
              className="text-white hover:text-pink-500 transition-colors duration-300"
            >
              <Instagram className="w-5 h-5" />
            </a>
            <a
              href={socialMedia.tiktok.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-tiktok"
              className="text-white hover:text-cyan-400 transition-colors duration-300"
            >
              <TikTokIcon className="w-5 h-5" />
            </a>
            <a
              href={socialMedia.facebook.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-facebook"
              className="text-white hover:text-blue-500 transition-colors duration-300"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href={socialMedia.youtube.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-youtube"
              className="text-white hover:text-red-500 transition-colors duration-300"
            >
              <Youtube className="w-5 h-5" />
            </a>
            
            {/* Language Selector */}
            <div className="border-l border-zinc-600 pl-4 ml-2">
              <LanguageSelector />
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center gap-4">
            <LanguageSelector />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              className="text-white hover:text-pink-500 transition-colors duration-300"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            data-testid="mobile-menu"
            className="lg:hidden mt-4 pb-4 bg-black/95 backdrop-blur-md rounded-lg border border-zinc-800"
          >
            <div className="flex flex-col py-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-bold py-3 px-6 tracking-wider uppercase transition-colors ${
                    isActive(link.path)
                      ? 'text-pink-500 bg-pink-500/10 border-l-4 border-pink-500'
                      : 'text-white hover:bg-white/5 hover:text-cyan-400'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="flex items-center gap-4 px-6 py-3">
                <a
                  href={socialMedia.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-pink-500"
                >
                  <Instagram className="w-5 h-5" />
                </a>
                <a
                  href={socialMedia.tiktok.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-cyan-400"
                >
                  <TikTokIcon className="w-5 h-5" />
                </a>
                <a
                  href={socialMedia.facebook.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-blue-500"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={socialMedia.youtube.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-red-500"
                >
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
